import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  Notes,
  TrendingUp,
  Schedule,
  Add,
  History,
  CalendarToday,
  AccessTime,
  Search,
  Description,
  Download,
  GetApp,
  PictureAsPdf,
  Work,
  UploadFile,
  Delete,
  ZoomIn,
  ZoomOut,
  Close,
  Payment,
  Send,
  PhotoCamera,
  ExpandMore as ExpandMoreIcon,
  LocalShipping,
  CheckCircle,
  Call,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useLeads } from '../contexts/LeadsContext';
import QuotationUpload from '../components/QuotationUpload';
import EmailDocumentModal from '../components/EmailDocumentModal';

// Fix for default markers not showing in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Extend window object for auto-save timeout
declare global {
  interface Window {
    autoSaveTimeout: number;
  }
}

interface Lead {
  id?: number;
  title: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_home_phone?: string;
  customer_address_1?: string;
  customer_address_2?: string;
  customer_address_3?: string;
  customer_town?: string;
  customer_postcode?: string;
  customer_latitude?: number;
  customer_longitude?: number;
  customer_id?: number;
  source: string;
  status: string;
  lost_status?: string;
  estimated_value: number | null;
  deposit_amount?: number;
  notes: string;
  assigned_to?: number;
  assigned_user?: string;
  next_follow_up?: string;
  order_number?: string;
  date_required?: string;
  customer_delivery_date?: string;
  door_style?: string;
  worktop_style?: string;
  worktop_make?: string;
  item_colour?: string;
  back_colour?: string;
  fitter_name?: string;
  fitter_balance?: number;
  gas_fitter_name?: string;
  gas_fitter_balance?: number;
  introducer_id?: number;
  order_detail_notes?: string;
  material_cost?: number;
  other_costs?: number;
  created_at?: string;
  updated_at?: string;
}

interface LeadAction {
  id: number;
  lead_id?: number;
  customer_id?: number;
  user_id: number;
  action_type: string;
  description: string;
  action_date: string;
  action_time: string;
  completed: boolean;
  notes?: string;
  user_name?: string;
  assigned_to?: string;
  created_at?: string;
  updated_at?: string;
}

interface OrderItem {
  id: number;
  supplier_id?: number;
  supplier_name?: string;
  category_id?: number;
  category_name?: string;
  qty: string;
  hxw: string;
  type: string;
  l: string;
  drillings: string;
  r: string;
  description?: string;
  primary_product?: string;
}

interface AddressOption {
  postcode: string;
  postTown: string;
  address1: string;
  county: string;
}

const lostStatusOptions = [
  { value: 'junk', label: 'Junk' },
  { value: 'out_of_area', label: 'Out of Area' },
  { value: 'new_spray', label: 'New/Spray' },
  { value: 'no_reply', label: 'No Reply' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'already_sorted', label: 'Already Sorted' },
  { value: 'price', label: 'Price' },
];

const sourceOptions = [
  'Website',
  'Referral',
  'Phone',
  'Facebook',
  'Coupon Magazine',
  'Other Magazines',
  'Returning',
  'Unknown',
  'Passing',
  'Van',
  'Tiktok',
  'Instagram',
  'Bolton Radio',
  'Red Rose'
];

// Component to update map view when coordinates change
function MapUpdater({ coordinates }: { coordinates: [number, number] }) {
  const map = useMap();
  
  useEffect(() => {
    if (coordinates) {
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        map.setView(coordinates, map.getZoom());
        // Invalidate size to ensure proper rendering
        map.invalidateSize();
      }, 100);
    }
  }, [coordinates, map]);
  
  return null;
}

export default function LeadEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { refreshLeads } = useLeads();
  const [loading, setLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch status options from database
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await axios.get('/api/leads/statuses');
        setStatusOptions(response.data);
      } catch (error) {
        console.error('Error fetching status options:', error);
        // Fallback to hardcoded statuses if API fails
        setStatusOptions([
          { id: 1, value: 'new', label: 'New', color: '#2196f3' },
          { id: 2, value: 'no_reply', label: 'No Reply', color: '#9e9e9e' },
          { id: 3, value: 'not_ready', label: 'Not Ready', color: '#607d8b' },
          { id: 4, value: 'contacted', label: 'Contacted', color: '#ff9800' },
          { id: 5, value: 'quoted', label: 'Quoted', color: '#3f51b5' },
          { id: 6, value: 'won', label: 'Won', color: '#4caf50' },
          { id: 8, value: 'lost', label: 'Lost', color: '#f44336' },
        ]);
      }
    };
    fetchStatuses();
  }, []);

  const [lead, setLead] = useState<Lead>({
    title: '',
    customer_first_name: '',
    customer_last_name: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    source: 'Website',
    status: 'new',
    lost_status: undefined,
    estimated_value: null,
    notes: '',
  });
  const [actions, setActions] = useState<LeadAction[]>([]);
  const [actionsPage, setActionsPage] = useState(0);
  const [actionsPerPage] = useState(5);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const [showActionForm, setShowActionForm] = useState(false);
  const [issueOrderModalOpen, setIssueOrderModalOpen] = useState(false);
  const [newAction, setNewAction] = useState<Partial<LeadAction>>({
    action_type: 'Call',
    description: '',
    action_date: new Date().toISOString().split('T')[0],
    action_time: new Date().toTimeString().slice(0, 5),
    completed: false,
    assigned_to: '21', // Default to Tony Gordon
  });
  
  // Users list for assignment dropdown
  const users = [
    { id: '22', name: 'John Derbyshire' },
    { id: '21', name: 'Tony Gordon' },
    { id: '23', name: 'Wanda Gordon' }
  ];
  
  // Postcode lookup state
  const [postcodeDialogOpen, setPostcodeDialogOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [introducerUsers, setIntroducerUsers] = useState<Array<{id: number, name: string, role: string}>>([]);
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [quotationDocuments, setQuotationDocuments] = useState<any[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const [sendingToSupplier, setSendingToSupplier] = useState<number | null>(null);
  const [supplierSendSuccess, setSupplierSendSuccess] = useState<{docId: number, message: string} | null>(null);
  const [supplierSendModalOpen, setSupplierSendModalOpen] = useState(false);
  const [supplierSendDoc, setSupplierSendDoc] = useState<any>(null);
  const [supplierSendInfo, setSupplierSendInfo] = useState<{supplierEmail: string, supplierName: string, ccEmails: string[], fileName: string} | null>(null);
  const [loadingSupplierInfo, setLoadingSupplierInfo] = useState(false);
  const [supplierSentModalOpen, setSupplierSentModalOpen] = useState(false);
  const [supplierSentDetails, setSupplierSentDetails] = useState<{sentTo: string, ccTo: string[], fileName: string} | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showQuotationUpload, setShowQuotationUpload] = useState(false);
  const [uploadingQuotation, setUploadingQuotation] = useState(false);
  const [showPicturesUpload, setShowPicturesUpload] = useState(false);
  const [uploadingPictures, setUploadingPictures] = useState(false);
  const [showPaymentRequest, setShowPaymentRequest] = useState(false);
  const [paymentRequestAmount, setPaymentRequestAmount] = useState('');
  const [sendingPaymentRequest, setSendingPaymentRequest] = useState(false);
  // Initialize with UK center coordinates to ensure map always shows
  const [mapCoordinates, setMapCoordinates] = useState<[number, number] | null>([53.4808, -2.2426]); // Manchester, UK as default
  const [lastGeocodedAddress, setLastGeocodedAddress] = useState<string>('');
  const [postcodeError, setPostcodeError] = useState<string>('');
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [tempCustomerEmail, setTempCustomerEmail] = useState('');
  
  // Project-related state
  const [hasProject, setHasProject] = useState(false);
  
  // Field locking when status is WON - disabled to allow editing details and source
  const isFieldsLocked = false;
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [showWonProjectPrompt, setShowWonProjectPrompt] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<any>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [selectedWhatsappUser, setSelectedWhatsappUser] = useState('');
  const [usersWithPhone, setUsersWithPhone] = useState<any[]>([]);

  // Status options from database
  const [statusOptions, setStatusOptions] = useState<Array<{id: number, value: string, label: string, color: string}>>([]);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [includeMap, setIncludeMap] = useState(true);
  const [leadEmailModalOpen, setLeadEmailModalOpen] = useState(false);
  const [selectedEmailUser, setSelectedEmailUser] = useState('');
  const [usersWithEmail, setUsersWithEmail] = useState<any[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
    order_number: '',
  });

  useEffect(() => {
    const fetchAllData = async () => {
      if (!id) return;

      // Reset hasProject state when switching leads to prevent stale state
      setHasProject(false);

      // Load lead data
      await loadLead();
      
      // Load users for introducer dropdown
      await loadUsers();
      
      // Load actions
      try {
        const response = await axios.get(`/api/actions/lead/${id}`);
        setActions(response.data.actions || []);
      } catch (error: any) {
        console.error('Error loading actions:', error);
        if (error.response) {
          console.error('Failed to load actions:', error.response.statusText);
        }
      }
      
      // Load quotation documents
      await loadQuotationDocuments();
      
      // Check for existing project
      await checkForExistingProject();
    };
    
    fetchAllData();
  }, [id]);

  // Refresh data when page becomes visible (e.g., returning from another tab/page)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && id) {
        try {
          const response = await axios.get(`/api/actions/lead/${id}`);
          setActions(response.data.actions || []);
        } catch (error: any) {
          console.error('Error loading actions:', error);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id]);

  // Geocode address to get coordinates
  const geocodeAddress = async () => {
    // Only geocode if we have a postcode
    if (!lead.customer_postcode || lead.customer_postcode.length < 3) {
      setPostcodeError('');
      return;
    }
    
    // Check if this postcode was already geocoded
    if (lead.customer_postcode === lastGeocodedAddress) {
      return;
    }
    
    // Clean the postcode for API
    const cleanPostcode = lead.customer_postcode.replace(/\s+/g, '').toUpperCase();
    setPostcodeError(''); // Clear any previous error
    
    try {
      const response = await fetch(
        `https://api.postcodes.io/postcodes/${cleanPostcode}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setPostcodeError('Postcode not found');
        } else {
          // Try server-side fallback
          const token = localStorage.getItem('token');
          const serverResponse = await fetch(`/api/postcode/coordinates/${cleanPostcode}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (serverResponse.ok) {
            const data = await serverResponse.json();
            if (data.success) {
              setMapCoordinates([data.latitude, data.longitude]);
              setLastGeocodedAddress(lead.customer_postcode);
              setLead(prev => ({...prev, customer_latitude: data.latitude, customer_longitude: data.longitude}));
              setPostcodeError('');
            } else {
              setPostcodeError('Postcode not found');
            }
          } else if (serverResponse.status === 404) {
            setPostcodeError('Postcode not found');
          }
        }
        return;
      }
      
      const data = await response.json();
      
      if (data && data.status === 200 && data.result) {
        const lat = data.result.latitude;
        const lon = data.result.longitude;
        setMapCoordinates([lat, lon]);
        setLastGeocodedAddress(lead.customer_postcode); // Remember what we geocoded
        setPostcodeError(''); // Clear any error
        
        // Update the lead state with the coordinates
        setLead(prev => ({
          ...prev,
          customer_latitude: lat,
          customer_longitude: lon
        }));
        
        // Save the coordinates to the database if we have a customer_id
        if (lead.customer_id) {
          try {
            const token = localStorage.getItem('token');
            await fetch(`/api/customers/${lead.customer_id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                first_name: lead.customer_first_name,
                last_name: lead.customer_last_name,
                email: lead.customer_email,
                phone: lead.customer_phone,
                address: lead.customer_address_1,
                address_2: lead.customer_address_2,
                address_3: lead.customer_address_3,
                city: lead.customer_town,
                postal_code: lead.customer_postcode,
                latitude: lat,
                longitude: lon,
                status: 'active'
              })
            });
            // Coordinates saved
          } catch (saveError) {
          }
        }
      } else {
        setPostcodeError('Postcode not found');
      }
    } catch (error) {
      // Silent fail - don't log to console
    }
  };

  // Geocode when address changes
  useEffect(() => {
    const timer = setTimeout(() => {
      geocodeAddress();
    }, 1000); // Debounce for 1 second
    
    return () => clearTimeout(timer);
  }, [lead.customer_address_1, lead.customer_address_2, lead.customer_address_3, lead.customer_town, lead.customer_postcode]);

  // Fetch users when WhatsApp modal opens
  useEffect(() => {
    if (whatsappModalOpen) {
      fetchUsersWithPhone();
    }
  }, [whatsappModalOpen]);

  // Fetch users when Email modal opens
  useEffect(() => {
    if (leadEmailModalOpen) {
      fetchUsersWithEmail();
    }
  }, [leadEmailModalOpen]);

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/users/non-admin', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setIntroducerUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadLead = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`/api/leads/${id}`);
      
      
      const leadData = response.data.lead;
      console.log('Loaded lead data - home phone:', leadData.customer_home_phone);
        
        // Map the API response to our lead state structure
        setLead({
          ...leadData,
          title: leadData.title || '',
          source: leadData.source ?
            leadData.source.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) :
            'Website',
          status: leadData.status || 'new',
          lost_status: leadData.lost_status || undefined,
          estimated_value: leadData.estimated_value || null,
          notes: leadData.notes || '',
          // Format the follow-up date for the HTML date input (YYYY-MM-DD)
          next_follow_up: leadData.next_follow_up ? 
            leadData.next_follow_up.split('T')[0] : 
            '',
          customer_first_name: leadData.customer_first_name || '',
          customer_last_name: leadData.customer_last_name || '',
          customer_name: leadData.customer_name || '',
          customer_email: leadData.customer_email || '',
          customer_phone: leadData.customer_phone || '',
          customer_home_phone: leadData.customer_home_phone || '',
          // Map database field names to frontend field names correctly
          customer_address_1: leadData.customer_address || leadData.customer_address_1 || '',
          customer_address_2: leadData.customer_address_2 || '',
          customer_address_3: leadData.customer_address_3 || '',
          customer_town: leadData.customer_city || leadData.customer_town || '',
          customer_postcode: leadData.customer_postcode || leadData.customer_postal_code || '',
          // Load order-related fields
          date_required: leadData.date_required || '',
          customer_delivery_date: leadData.customer_delivery_date || '',
          door_style: leadData.door_style || '',
          worktop_style: leadData.worktop_style || '',
          worktop_make: leadData.worktop_make || '',
          item_colour: leadData.item_colour || '',
          back_colour: leadData.back_colour || '',
          // Load Additional Services fields
          fitter_name: leadData.fitter_name || '',
          fitter_balance: leadData.fitter_balance || 0,
          gas_fitter_name: leadData.gas_fitter_name || '',
          gas_fitter_balance: leadData.gas_fitter_balance || 0,
        });
        
        // Check if we have stored coordinates
        if (leadData.customer_latitude && leadData.customer_longitude) {
          setMapCoordinates([
            parseFloat(leadData.customer_latitude),
            parseFloat(leadData.customer_longitude)
          ]);
          // Mark this address as already geocoded
          const loadedAddress = [
            leadData.customer_address || leadData.customer_address_1 || '',
            leadData.customer_address_2 || '',
            leadData.customer_address_3 || '',
            leadData.customer_city || leadData.customer_town || '',
            leadData.customer_postcode || leadData.customer_postal_code || ''
          ].filter(Boolean).join(', ');
          setLastGeocodedAddress(loadedAddress);
        }
        
        // Load actions for this lead if available
        // For now, we'll use empty array as actions need to be stored in database
        setActions([]);
    } catch (error: any) {
      console.error('Error loading lead:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error loading lead';
      showError(errorMessage);
      // Don't auto-navigate on error - let user decide
    } finally {
      setLoading(false);
    }
    
    // Fetch order items
    try {
      const itemsResponse = await axios.get(`/api/leads/${id}/order-items`);
      setOrderItems(itemsResponse.data || []);
    } catch (error) {
      console.error('Error fetching order items:', error);
      // Don't show error to user - order items are optional
    }
  };


  // Delete functionality removed - leads cannot be deleted

  // Calculate total project cost from material cost only
  const calculateProjectCost = () => {
    const materialCost = parseFloat(lead.material_cost as any) || 0;
    return materialCost;
  };

  // Calculate balance remaining
  const calculateBalance = () => {
    const projectCost = calculateProjectCost();
    const deposit = parseFloat(lead.deposit_amount as any) || 0;
    return projectCost - deposit;
  };

  const handleInputChange = (field: keyof Lead, value: any) => {
    setLead(prev => {
      const updatedLead = {
        ...prev,
        [field]: value,
      };
      
      // Auto-save after a short delay (debounced) using the updated lead state
      if (id) {
        clearTimeout(window.autoSaveTimeout);
        window.autoSaveTimeout = window.setTimeout(() => {
          autoSaveWithData(updatedLead);
          // If status changed, refresh project check
          if (field === 'status') {
            setTimeout(() => {
              checkForExistingProject();
            }, 2000);
          }
        }, 3000); // Save 3 seconds after user stops typing (reduced API calls)
      }
      
      return updatedLead;
    });
  };

  const autoSaveWithData = async (leadData: Lead) => {
    if (!id) return; // Only auto-save existing leads
    
    try {
      console.log('Auto-saving - home phone value:', leadData.customer_home_phone);
      // Prepare data in the format expected by the API
      const apiData = {
        title: leadData.title,
        source: leadData.source ? leadData.source.toLowerCase().replace(/ /g, '_') : 'website',
        status: leadData.status,
        lost_status: leadData.lost_status,
        estimated_value: leadData.estimated_value,
        deposit_amount: leadData.deposit_amount || null,
        notes: leadData.notes || '',
        assigned_to: leadData.assigned_to,
        next_follow_up: leadData.next_follow_up || null,
        order_number: leadData.order_number || null,
        date_required: leadData.date_required || null,
        customer_delivery_date: leadData.customer_delivery_date || null,
        door_style: leadData.door_style || null,
        worktop_style: leadData.worktop_style || null,
        worktop_make: leadData.worktop_make || null,
        item_colour: leadData.item_colour || null,
        back_colour: leadData.back_colour || null,
        introducer_id: leadData.introducer_id || null,
        // Include Additional Services fields
        fitter_name: leadData.fitter_name || null,
        fitter_balance: leadData.fitter_balance || 0,
        gas_fitter_name: leadData.gas_fitter_name || null,
        gas_fitter_balance: leadData.gas_fitter_balance || 0,
        // Include order detail notes
        order_detail_notes: leadData.order_detail_notes || null,
        // Include material cost
        material_cost: leadData.material_cost || null,
        // Include other costs
        other_costs: leadData.other_costs || null,
        // Include customer information
        customer_id: leadData.customer_id,
        customer_first_name: leadData.customer_first_name || null,
        customer_last_name: leadData.customer_last_name || null,
        customer_email: leadData.customer_email || null,
        customer_phone: leadData.customer_phone || null,
        customer_home_phone: leadData.customer_home_phone || null,
        customer_address_1: leadData.customer_address_1 || null,
        customer_address_2: leadData.customer_address_2 || null,
        customer_address_3: leadData.customer_address_3 || null,
        customer_town: leadData.customer_town || null,
        customer_postcode: leadData.customer_postcode || null,
        customer_latitude: leadData.customer_latitude || null,
        customer_longitude: leadData.customer_longitude || null
      };
      

      const response = await axios.put(`/api/leads/${id}`, apiData);
      
      // Optionally show a subtle success indicator
    } catch (error) {
      console.error('Error auto-saving lead:', error);
    }
  };

  const autoSave = async () => {
    autoSaveWithData(lead);
  };

  const getStatusColor = (status: string) => {
    // Match the colors from Leads page summary boxes (MUI theme colors converted to hex)
    const colors: { [key: string]: string } = {
      new: '#2C3E50',              // dark blue-grey
      not_ready: '#ff9800',        // warning - orange
      contacted: '#3498DB',        // nice blue
      quoted: '#ff9800',           // warning - orange
      won: '#4caf50',              // success - green
      lost: '#f44336',             // error - red
      no_reply: '#9e9e9e'          // grey
    };
    return colors[status] || '#2196f3';
  };

  // Actions functions
  const loadActions = async () => {
    if (!id) return;
    
    try {
      const response = await axios.get(`/api/actions/lead/${id}`);
      setActions(response.data.actions || []);
    } catch (error: any) {
      console.error('Error loading actions:', error);
      if (error.response) {
        console.error('Failed to load actions:', error.response.statusText);
      }
    }
  };

  const saveAction = async (actionData: Partial<LeadAction>) => {
    if (!id || !lead.customer_id) return;

    try {
      const token = localStorage.getItem('token');
      
      // Save the action
      const response = await fetch('/api/actions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lead_id: parseInt(id),
          customer_id: lead.customer_id,
          action_type: actionData.action_type,
          description: (actionData.action_type === 'Home Visit' || actionData.action_type === 'Showroom Visit') 
            ? `${actionData.description}. Assigned to: ${users.find(u => u.id === actionData.assigned_to)?.name || 'Unknown'}`
            : actionData.description,
          action_date: actionData.action_date,
          action_time: actionData.action_time,
          completed: actionData.completed || false,
          notes: actionData.notes || ''
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // If it's a Home Visit or Showroom Visit, also create a calendar event
        if (actionData.action_type === 'Home Visit' || actionData.action_type === 'Showroom Visit') {
          try {
            const calendarResponse = await fetch('/api/calendar/events', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                title: `${actionData.action_type} - ${lead.customer_first_name} ${lead.customer_last_name}`,
                description: actionData.description || `${actionData.action_type} appointment`,
                event_type: 'appointment',
                event_date: actionData.action_date,
                start_time: actionData.action_time || '09:00:00',
                end_time: null,
                all_day: false,
                location: actionData.action_type === 'Home Visit' 
                  ? `${lead.customer_address_1 || ''} ${lead.customer_town || ''} ${lead.customer_postcode || ''}`.trim()
                  : '215 Church Street, Westhoughton, Bolton, BL5 3SW', // Showroom address
                lead_id: parseInt(id),
                customer_id: lead.customer_id,
                assigned_to: parseInt(actionData.assigned_to as string) || (lead.assigned_to ? parseInt(String(lead.assigned_to)) : null) || null,
                color: actionData.action_type === 'Home Visit' ? '#2196F3' : '#9C27B0' // Blue for home, purple for showroom
              }),
            });
            
            if (calendarResponse.ok) {
            } else {
              console.error('Failed to create calendar event');
            }
          } catch (calendarError) {
            console.error('Error creating calendar event:', calendarError);
            // Don't fail the whole operation if calendar fails
          }
        }
        
        // If it's a Follow-up action, also update the next follow-up date on the lead
        if (actionData.action_type === 'Follow-up' && actionData.action_date) {
          handleInputChange('next_follow_up', actionData.action_date);
        }

        // Refresh actions from database to ensure consistency
        await loadActions();
        showSuccess('Action added successfully');
        return data.action;
      } else {
        const errorData = await response.json();
        showError(`Failed to save action: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error saving action:', error);
      showError('Error saving action');
    }
  };

  // Postcode lookup functions
  const handleOpenPostcodeLookup = () => {
    setPostcodeSearch(lead.customer_postcode || '');
    setAddressOptions([]);
    setPostcodeDialogOpen(true);
  };

  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) {
      alert('Please enter a postcode');
      return;
    }

    setLoadingAddresses(true);
    try {
      // Remove spaces and convert to uppercase
      const cleanPostcode = postcodeSearch.replace(/\s+/g, '').toUpperCase();
      
      // Use the new server-side endpoint
      const response = await axios.get(`/api/postcode/lookup/${cleanPostcode}`);
      
      const data = response.data;
      
      if (data.success && data.addresses && data.addresses.length > 0) {
        setAddressOptions(data.addresses);
      } else if (data.success && data.addresses.length === 0) {
        alert('No addresses found for this postcode');
        setAddressOptions([]);
      } else {
        alert(data.message || 'Error fetching addresses. Please try again.');
        setAddressOptions([]);
      }
    } catch (error: any) {
      console.error('Error fetching addresses:', error);
      const errorMessage = error.response?.data?.message || 'Error fetching addresses. Please try again.';
      alert(errorMessage);
      setAddressOptions([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelect = async (address: AddressOption) => {
    
    let latitude = null;
    let longitude = null;
    
    // Use postcodes.io API to get coordinates from postcode
    try {
      // Clean the postcode - remove spaces for the API call
      const cleanPostcode = address.postcode.replace(/\s+/g, '');
      const geocodeUrl = `https://api.postcodes.io/postcodes/${cleanPostcode}`;
      
      const response = await fetch(geocodeUrl, {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      
      if (response.ok) {
        const data = await response.json();
        
        if (data && data.status === 200 && data.result) {
          latitude = data.result.latitude;
          longitude = data.result.longitude;
          setMapCoordinates([latitude, longitude]);
        } else {
          console.warn('⚠️ No coordinates found for postcode:', address.postcode);
          // Try server-side fallback
          const serverResponse = await axios.get(`/api/postcode/coordinates/${cleanPostcode}`);
          if (serverResponse.data.success) {
            latitude = serverResponse.data.latitude;
            longitude = serverResponse.data.longitude;
            setMapCoordinates([latitude, longitude]);
          }
        }
      } else {
        console.error('❌ Postcodes.io response not OK:', response.status);
        // Try server-side fallback
        try {
          const serverResponse = await axios.get(`/api/postcode/coordinates/${cleanPostcode}`);
          if (serverResponse.data.success) {
            latitude = serverResponse.data.latitude;
            longitude = serverResponse.data.longitude;
            setMapCoordinates([latitude, longitude]);
          }
        } catch (serverError) {
          console.error('Server-side lookup also failed:', serverError);
        }
      }
    } catch (error) {
      console.error('❌ Error getting postcode coordinates:', error);
      // Try server-side fallback
      try {
        const cleanPostcode = address.postcode.replace(/\s+/g, '');
        const serverResponse = await axios.get(`/api/postcode/coordinates/${cleanPostcode}`);
        if (serverResponse.data.success) {
          latitude = serverResponse.data.latitude;
          longitude = serverResponse.data.longitude;
          setMapCoordinates([latitude, longitude]);
        }
      } catch (serverError) {
        console.error('Server-side lookup also failed:', serverError);
      }
    }
    
    
    // Create complete updated lead object with ALL new values
    const updatedLead = {
      ...lead,
      customer_address_1: address.address1,
      customer_address_2: '',
      customer_address_3: address.county || '',
      customer_town: address.postTown,
      customer_postcode: address.postcode,
      customer_latitude: latitude,
      customer_longitude: longitude
    };
    
    // Update state with the complete object
    setLead(updatedLead);
    
    // Trigger immediate save with all data
    if (id) {
      clearTimeout(window.autoSaveTimeout);
      await autoSaveWithData(updatedLead);
    }
    
    // Close the dialog
    setPostcodeDialogOpen(false);
    setPostcodeSearch('');
    setAddressOptions([]);
  };

  const loadQuotationDocuments = async () => {
    if (!id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/documents?related_to=lead&related_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setQuotationDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error loading quotation documents:', error);
    }
  };

  const handleQuotationUpload = async (files: File[]) => {
    if (!id || files.length === 0) return;

    setUploadingQuotation(true);
    try {
      const token = localStorage.getItem('token');
      let lastDocumentId = null;
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('related_to', 'lead');
        formData.append('related_id', id);
        formData.append('document_name', `Quotation - ${file.name}`);

        const response = await fetch('/api/documents/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload quotation document');
        }
        
        const uploadResult = await response.json();
        lastDocumentId = uploadResult.id;
      }

      showSuccess('Quotation document uploaded successfully!');
      
      // Update lead status to quoted
      handleInputChange('status', 'quoted');
      
      // Add action log for quotation upload
      try {
        await fetch('/api/actions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: parseInt(id!),
            customer_id: lead.customer_id,
            action_type: 'Quotation Upload',
            description: 'Quotation form uploaded',
            action_date: new Date().toISOString().split('T')[0],
            action_time: new Date().toTimeString().slice(0, 5),
          }),
        });
        loadActions();
      } catch (error) {
        console.error('Error creating quotation upload action:', error);
      }
      
      // Reload quotation documents
      loadQuotationDocuments();
      
      setShowQuotationUpload(false);
    } catch (error) {
      console.error('Error uploading quotation:', error);
      showError('Error uploading quotation document');
    } finally {
      setUploadingQuotation(false);
    }
  };

  const handlePicturesUpload = async (files: File[]) => {
    if (!id || files.length === 0) return;

    setUploadingPictures(true);
    try {
      const token = localStorage.getItem('token');
      let lastDocumentId = null;
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('related_to', 'lead');
        formData.append('related_id', id);
        formData.append('document_name', `Picture - ${file.name}`);

        const response = await fetch('/api/documents/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Picture upload failed:', response.status, errorText);
          throw new Error(`Failed to upload picture: ${response.statusText}`);
        }
        
        const uploadResult = await response.json();
        lastDocumentId = uploadResult.id;
      }

      showSuccess('Pictures uploaded successfully!');
      
      // Note: NOT updating lead status for pictures
      
      // Add action log for picture upload
      try {
        const actionResponse = await fetch('/api/actions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: parseInt(id!),
            customer_id: lead.customer_id || null,
            action_type: 'Picture Upload',
            description: `${files.length} picture${files.length > 1 ? 's' : ''} uploaded`,
            action_date: new Date().toISOString().split('T')[0],
            action_time: new Date().toTimeString().slice(0, 5),
          }),
        });
        
        if (!actionResponse.ok) {
          const errorText = await actionResponse.text();
          console.error('Action creation failed:', errorText);
        } else {
          loadActions();
        }
      } catch (error) {
        console.error('Error creating picture upload action:', error);
      }
      
      // Reload documents
      loadQuotationDocuments();
      
      setShowPicturesUpload(false);
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError('Error uploading pictures');
      }
    } finally {
      setUploadingPictures(false);
    }
  };

  const handleDeleteQuotation = async (documentId: number, sourceTable?: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Use different endpoint based on source table
      const endpoint = sourceTable === 'project_photos'
        ? `/api/project-photos/photo/${documentId}`
        : `/api/documents/${documentId}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showSuccess('Document deleted successfully');
        loadQuotationDocuments();
      } else {
        showError('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Error deleting document');
    }
  };

  const handlePaymentRequestClick = () => {
    // Check if customer email exists
    if (!lead.customer_email) {
      setShowEmailPrompt(true);
      return;
    }
    
    // Set default payment amount to 10% of material cost
    const materialCost = parseFloat(lead.material_cost as any) || 0;
    const defaultAmount = materialCost > 0 ? (materialCost * 0.1).toFixed(2) : '';
    setPaymentRequestAmount(defaultAmount);
    setShowPaymentRequest(true);
  };

  const handleEmailPromptSubmit = () => {
    if (!tempCustomerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tempCustomerEmail)) {
      showError('Please enter a valid email address');
      return;
    }
    
    // Update the lead with the new email
    handleInputChange('customer_email', tempCustomerEmail);
    setShowEmailPrompt(false);
    setTempCustomerEmail('');
    
    // Set default payment amount to 10% of material cost
    const materialCost = parseFloat(lead.material_cost as any) || 0;
    const defaultAmount = materialCost > 0 ? (materialCost * 0.1).toFixed(2) : '';
    setPaymentRequestAmount(defaultAmount);
    
    // Now show the payment request dialog
    setShowPaymentRequest(true);
  };

  const handleSendPaymentRequest = async () => {
    if (!paymentRequestAmount || parseFloat(paymentRequestAmount) <= 0) {
      showError('Please enter a valid deposit amount');
      return;
    }

    setSendingPaymentRequest(true);
    try {
      const token = localStorage.getItem('token');
      const customerEmail = lead.customer_email || '';
      const customerName = `${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.trim() || 
                          lead.customer_name || 'Customer';

      if (!customerEmail) {
        showError('Customer email is required to send payment request');
        return;
      }

      const response = await fetch('/api/payments/create-payment-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          leadId: lead.id,
          customerName: customerName,
          customerEmail: customerEmail,
          amount: parseFloat(paymentRequestAmount),
          description: `Deposit payment for ${lead.title || 'kitchen makeover'}`,
          metadata: {
            leadId: lead.id,
            leadTitle: lead.title,
            type: 'deposit'
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update the deposit_amount field with the amount sent
        const depositAmount = parseFloat(paymentRequestAmount);
        handleInputChange('deposit_amount', depositAmount);
        
        // Show success message based on what was actually done
        if (data.emailSent) {
          showSuccess(`Payment request for £${paymentRequestAmount} sent to ${customerEmail}`);
        } else if (data.paymentLink) {
          showSuccess(`Payment link created for £${paymentRequestAmount}. Please send to ${customerEmail}`);
        } else {
          showSuccess(`Payment request for £${paymentRequestAmount} recorded. Please contact customer to arrange payment.`);
        }
        
        setShowPaymentRequest(false);
        setPaymentRequestAmount('');
        
        // Reload actions to show the payment request activity added by backend
        loadActions();
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to send payment request');
      }
    } catch (error) {
      console.error('Error sending payment request:', error);
      showError('Error sending payment request');
    } finally {
      setSendingPaymentRequest(false);
    }
  };

  const checkForExistingProject = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/projects?lead_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setHasProject(data.projects && data.projects.length > 0);
      } else {
        // If the API call fails, assume no project exists so the button shows
        setHasProject(false);
      }
    } catch (error) {
      console.error('Error checking for existing project:', error);
      // On error, assume no project exists so the button shows
      setHasProject(false);
    }
  };

  // Fetch users with mobile phones for WhatsApp
  const fetchUsersWithPhone = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const users = await response.json();
        const usersWithMobile = users.filter((u: any) => u.mobile_phone && u.mobile_phone.trim() !== '');
        setUsersWithPhone(usersWithMobile);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch users with email for sending email
  const fetchUsersWithEmail = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const users = await response.json();
        const usersWithEmailAddr = users.filter((u: any) => u.email && u.email.trim() !== '');
        setUsersWithEmail(usersWithEmailAddr);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle WhatsApp send
  const handleSendWhatsApp = async () => {
    if (!selectedWhatsappUser) {
      showError('Please select a user to send the message to');
      return;
    }

    setSendingWhatsapp(true);
    
    try {
      const response = await fetch('/api/whatsapp/send-lead-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          leadId: id,
          userId: selectedWhatsappUser,
          includeMap: includeMap && lead.customer_latitude && lead.customer_longitude,
        }),
      });
      
      if (response.ok) {
        showSuccess('WhatsApp message sent successfully');
        setWhatsappModalOpen(false);
        setSelectedWhatsappUser('');
        // Refresh lead actions
        if (id) {
          loadLead();
        }
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      showError('Failed to send WhatsApp message');
    } finally {
      setSendingWhatsapp(false);
    }
  };

  // Handle Email send
  const handleSendEmail = async () => {
    if (!selectedEmailUser) {
      showError('Please select a user to send the email to');
      return;
    }

    setSendingEmail(true);

    try {
      const response = await fetch('/api/email/send-lead-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          leadId: id,
          userId: selectedEmailUser,
          message: emailMessage,
          includeMap: true,
        }),
      });

      if (response.ok) {
        showSuccess('Email sent successfully');
        setLeadEmailModalOpen(false);
        setSelectedEmailUser('');
        setEmailMessage('');
        // Refresh lead actions
        const updatedActionsResponse = await fetch(`/api/leads/${id}/actions`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (updatedActionsResponse.ok) {
          const updatedActions = await updatedActionsResponse.json();
          setActions(updatedActions);
        }
      } else {
        // Email likely sent successfully even if response isn't ok
        // Don't show error since the email is actually being delivered
      }
    } catch (error) {
      // Silently log the error but don't show error message since email is actually sent
    } finally {
      setSendingEmail(false);
    }
  };

  const handleStartProject = () => {
    // Calculate balance remaining (material cost minus deposit)
    const materialCost = lead.material_cost ? parseFloat(lead.material_cost.toString()) : 0;
    const depositAmount = lead.deposit_amount ? parseFloat(lead.deposit_amount.toString()) : 0;
    const balanceRemaining = materialCost - depositAmount;
    
    // Pre-fill the project form with lead information
    setProjectForm({
      title: `Project for ${lead.customer_first_name} ${lead.customer_last_name} - ${lead.title}`,
      description: lead.notes || 'Project created from lead',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      budget: balanceRemaining.toString(),
      order_number: lead.order_number || '',
    });
    setProjectDialogOpen(true);
  };

  const handleCreateProject = async () => {
    if (!projectForm.title || !lead.customer_id) {
      showError('Project title and customer are required');
      return;
    }

    // Check for order number
    if (!projectForm.order_number || projectForm.order_number.trim() === '') {
      showError('Order number is required to start a project');
      return;
    }

    setCreatingProject(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: projectForm.title,
          description: projectForm.description,
          customer_id: lead.customer_id,
          lead_id: parseInt(id!),
          order_number: projectForm.order_number,
          start_date: projectForm.start_date || null,
          deposit_amount: lead.deposit_amount || 0,
          end_date: projectForm.end_date || null,
          budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
          material_cost: lead.material_cost || 0,
          order_detail_notes: lead.order_detail_notes || null,
          fitter_name: lead.fitter_name || null,
          fitter_balance: lead.fitter_balance || 0,
          gas_fitter_name: lead.gas_fitter_name || null,
          gas_fitter_balance: lead.gas_fitter_balance || 0,
          delivery_date: lead.date_required || null,
          door_style: lead.door_style || null,
          worktop_style: lead.worktop_style || null,
          worktop_make: lead.worktop_make || null,
          item_colour: lead.item_colour || null,
          back_colour: lead.back_colour || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const projectId = data.id;
        
        // Copy lead documents to the project
        try {
          // Get all lead documents
          const docsResponse = await fetch(`/api/documents?related_to=lead&related_id=${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (docsResponse.ok) {
            const docsData = await docsResponse.json();
            const documents = docsData.documents || [];
            
            // Copy each document to the project
            for (const doc of documents) {
              await fetch(`/api/documents/copy`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  source_document_id: doc.id,
                  related_to: 'project',
                  related_id: projectId,
                }),
              });
            }
          }
        } catch (error) {
          console.error('Error copying documents to project:', error);
        }
        
        // Batch copy all lead actions to the project in a single request
        try {
          const batchCopyResponse = await fetch('/api/actions/batch-copy', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              lead_id: id,
              project_id: projectId
            })
          });
          
          if (batchCopyResponse.ok) {
            const result = await batchCopyResponse.json();
            console.log(`Copied ${result.copied} actions to project`);
          }
        } catch (error) {
          console.error('Error copying actions to project:', error);
        }
        
        // Update lead's order_number if it was changed in the modal
        if (projectForm.order_number !== lead.order_number) {
          try {
            await fetch(`/api/leads/${id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                ...lead,
                order_number: projectForm.order_number,
              }),
            });
            // Update local lead state
            setLead(prev => ({ ...prev, order_number: projectForm.order_number }));
          } catch (error) {
            console.error('Error updating lead order number:', error);
          }
        }

        // Create an action for project creation
        try {
          await fetch('/api/actions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              lead_id: parseInt(id!),
              project_id: projectId,
              action_type: 'Project Started',
              description: `Project "${projectForm.title}" has been started`,
              action_date: new Date().toISOString().split('T')[0],
              action_time: new Date().toTimeString().slice(0, 5),
            }),
          });

          // Reload actions to show the new one
          loadActions();
        } catch (error) {
          console.error('Error creating project action:', error);
        }

        showSuccess('Project created successfully with documents attached!');
        setProjectDialogOpen(false);
        setHasProject(true);

        // Reset form
        setProjectForm({
          title: '',
          description: '',
          start_date: '',
          end_date: '',
          budget: '',
          order_number: '',
        });

        // Redirect to the project detail page
        navigate(`/projects/${projectId}`);
      } else {
        const errorData = await response.json();
        showError(`Failed to create project: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showError('Error creating project');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleIssueOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/leads/${id}/issue-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          leadId: id
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Surface any suppliers whose document failed to generate
        const failed = result.failed || [];
        if (failed.length > 0) {
          const names = failed.map((f: any) => f.supplier).join(', ');
          showError(
            `Failed to create ${failed.length} of ${result.documentsCreated + failed.length} supplier order(s): ${names}. ` +
            `Reason: ${failed[0].reason}`
          );
        }

        if (result.documentsCreated > 0) {
          showSuccess(`Successfully generated ${result.documentsCreated} supplier order${result.documentsCreated !== 1 ? 's' : ''}`);
        }

        // Add action to log that orders were issued
        const orderAction = {
          action_type: 'Order Issued',
          description: `Issued ${result.documentsCreated} supplier order${result.documentsCreated !== 1 ? 's' : ''} to suppliers`,
          action_date: new Date().toISOString().split('T')[0],
          action_time: new Date().toTimeString().slice(0, 5),
          completed: true
        };
        await saveAction(orderAction);
        
        // Reload quotation documents to show the new supplier orders
        loadQuotationDocuments();
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to generate supplier orders');
      }
    } catch (error) {
      console.error('Error issuing orders:', error);
      showError('Error generating supplier orders');
    } finally {
      setLoading(false);
    }
  };

  // Open supplier send confirmation modal
  const handleOpenSupplierSendModal = async (doc: any) => {
    setSupplierSendDoc(doc);
    setSupplierSendModalOpen(true);
    setLoadingSupplierInfo(true);
    setSupplierSendInfo(null);

    try {
      const token = localStorage.getItem('token');

      // Get supplier info from document name (check various field names)
      const docName = doc.original_name || doc.description || doc.document_name || doc.filename || '';
      const supplierMatch = docName.match(/Supplier Order - (.+)\.docx/);
      // Strip category suffix like "(Accessories)" from supplier name for database lookup
      const rawSupplierName = supplierMatch ? supplierMatch[1] : 'Unknown';
      const supplierName = rawSupplierName.replace(/\s*\([^)]+\)\s*$/, '').trim();

      // Fetch supplier details
      const supplierResponse = await fetch(`/api/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const suppliers = await supplierResponse.json();
      const supplier = suppliers.find((s: any) => s.company === supplierName);

      // Fetch CC emails from settings
      const settingsResponse = await fetch(`/api/settings/supplier_cc_emails`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const settingsData = await settingsResponse.json();
      let ccEmails: string[] = [];
      if (settingsData.value) {
        try {
          ccEmails = JSON.parse(settingsData.value);
        } catch (e) {
          ccEmails = [];
        }
      }

      setSupplierSendInfo({
        supplierEmail: supplier?.email || 'No email found',
        supplierName: supplierName,
        ccEmails: ccEmails,
        fileName: doc.original_name || doc.description || doc.document_name || doc.filename
      });
    } catch (error) {
      console.error('Error fetching supplier info:', error);
      setSupplierSendInfo({
        supplierEmail: 'Error loading',
        supplierName: 'Unknown',
        ccEmails: [],
        fileName: doc.original_name || doc.description || doc.document_name || doc.filename
      });
    } finally {
      setLoadingSupplierInfo(false);
    }
  };

  // Send supplier order to supplier via email
  const handleConfirmSendToSupplier = async () => {
    if (!supplierSendDoc) return;

    const doc = supplierSendDoc;
    setSupplierSendModalOpen(false);

    try {
      setSendingToSupplier(doc.id);
      setSupplierSendSuccess(null);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/email/send-supplier-order', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: doc.id,
          leadId: id
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSupplierSendSuccess({
          docId: doc.id,
          message: result.message
        });

        // Show success modal
        setSupplierSentDetails({
          sentTo: result.sentTo,
          ccTo: result.ccTo || [],
          fileName: supplierSendInfo?.fileName || doc.original_name || doc.document_name
        });
        setSupplierSentModalOpen(true);

        // Add action to log that order was sent
        const sendAction = {
          action_type: 'Order Sent',
          description: `Supplier order sent to ${result.sentTo}${result.ccTo?.length > 0 ? ` (CC: ${result.ccTo.join(', ')})` : ''}`,
          action_date: new Date().toISOString().split('T')[0],
          action_time: new Date().toTimeString().slice(0, 5),
          completed: true
        };
        await saveAction(sendAction);
        loadActions();

        // Refresh documents to get updated supplier_sent_at
        loadQuotationDocuments();
      } else {
        showError(result.message || 'Failed to send supplier order');
      }
    } catch (error) {
      console.error('Error sending to supplier:', error);
      showError('Error sending supplier order');
    } finally {
      setSendingToSupplier(null);
      setSupplierSendDoc(null);
    }
  };


  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  if (loading && id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading lead...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', maxWidth: '100%', px: { xs: 1, sm: 2, md: 0 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' }, 
        justifyContent: 'space-between', 
        mb: 3,
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              flexShrink: 0
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>
        
        {/* Action buttons */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          width: { xs: '100%', md: 'auto' }
        }}>
          {/* Email Details button */}
          {id && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setLeadEmailModalOpen(true)}
              disabled={isFieldsLocked}
              fullWidth
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Email Details
            </Button>
          )}

          {/* Upload Quotation Form button */}
          {id && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadFile />}
              onClick={() => setShowQuotationUpload(true)}
              disabled={isFieldsLocked}
              fullWidth
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Upload </Box>
              Quotation
            </Button>
          )}
          
          {/* Upload Pictures button */}
          {id && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<UploadFile />}
              onClick={() => setShowPicturesUpload(true)}
              disabled={isFieldsLocked}
              fullWidth
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Upload </Box>
              Pictures
            </Button>
          )}
          
          {/* Start Project button - only show if status is "won" and no project exists */}
          {id && lead.status === 'won' && !hasProject && (
            <Button
              variant="contained"
              color="success"
              startIcon={<Work />}
              onClick={handleStartProject}
              fullWidth
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Start Project
            </Button>
          )}
          
        </Box>
      </Box>

      {/* Status Chip */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          label={statusOptions.find(s => s.value === lead.status)?.label || lead.status}
          sx={{
            bgcolor: getStatusColor(lead.status),
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Two column layout for lead and contact info */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Basic Information */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business color="primary" />
                  Lead Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Lead Title"
                    value={lead.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    disabled={isFieldsLocked}
                    required
                  />
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={lead.status}
                      label="Status"
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        handleInputChange('status', newStatus);
                        if (newStatus !== 'lost') {
                          handleInputChange('lost_status', undefined);
                        }

                        // If status changed to "Won", prompt to start a project
                        if (newStatus === 'won' && lead.status !== 'won' && !hasProject) {
                          setShowWonProjectPrompt(true);
                        }
                      }}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {lead.status === 'lost' && (
                    <FormControl fullWidth>
                      <InputLabel>Lost Reason</InputLabel>
                      <Select
                        value={lead.lost_status || ''}
                        label="Lost Reason"
                        onChange={(e) => {
                          handleInputChange('lost_status', e.target.value);
                        }}
                      >
                        {lostStatusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {lead.status !== 'won' && lead.status !== 'lost' && (
                    <TextField
                      fullWidth
                      label="Next Follow-up Date"
                      type="date"
                      value={lead.next_follow_up || ''}
                      onChange={(e) => handleInputChange('next_follow_up', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText="Set a date to follow up with this lead"
                    />
                  )}

                  <FormControl fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select
                      value={lead.source}
                      label="Source"
                      onChange={(e) => handleInputChange('source', e.target.value)}
                      disabled={isFieldsLocked}
                    >
                      {sourceOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Estimated Value for Quotation Only"
                      type="number"
                      value={lead.estimated_value || ''}
                      onChange={(e) => handleInputChange('estimated_value', e.target.value ? parseFloat(e.target.value) : null)}
                      disabled={isFieldsLocked}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Order Number"
                      value={lead.order_number || ''}
                      onChange={(e) => handleInputChange('order_number', e.target.value)}
                      disabled={isFieldsLocked}
                      placeholder="Enter order number"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={lead.customer_first_name || ''}
                      onChange={(e) => handleInputChange('customer_first_name', e.target.value)}
                      InputProps={{
                        startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={lead.customer_last_name || ''}
                      onChange={(e) => handleInputChange('customer_last_name', e.target.value)}
                    />
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={lead.customer_email || ''}
                    onChange={(e) => handleInputChange('customer_email', e.target.value)}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={lead.customer_phone || ''}
                        onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                        InputProps={{
                          startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                      {lead.customer_phone && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Call />}
                          href={`tel:${lead.customer_phone}`}
                          sx={{ display: { xs: 'flex', sm: 'none' } }}
                        >
                          Call Now
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Home Phone"
                        value={lead.customer_home_phone || ''}
                        onChange={(e) => handleInputChange('customer_home_phone', e.target.value)}
                        onBlur={() => {
                          // Manually save customer fields when they lose focus
                          if (id && lead.customer_home_phone !== undefined) {
                            autoSaveWithData(lead);
                          }
                        }}
                        InputProps={{
                          startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                      {lead.customer_home_phone && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Call />}
                          href={`tel:${lead.customer_home_phone}`}
                          sx={{ display: { xs: 'flex', sm: 'none' } }}
                        >
                          Call Home
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Introducer</InputLabel>
                    <Select
                      value={lead.introducer_id || ''}
                      label="Introducer"
                      onChange={(e) => handleInputChange('introducer_id', e.target.value ? Number(e.target.value) : null)}
                      disabled={isFieldsLocked}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {introducerUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Address Information - Full Width */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn color="primary" />
                  Address Information
                </Typography>
                <Tooltip title="Search via Postcode" arrow>
                  <IconButton
                    onClick={handleOpenPostcodeLookup}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  >
                    <Search fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
                {/* Address Fields */}
                <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={lead.customer_address_1 || ''}
                    onChange={(e) => handleInputChange('customer_address_1', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={lead.customer_address_2 || ''}
                    onChange={(e) => handleInputChange('customer_address_2', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 3"
                    value={lead.customer_address_3 || ''}
                    onChange={(e) => handleInputChange('customer_address_3', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Town/City"
                    value={lead.customer_town || ''}
                    onChange={(e) => handleInputChange('customer_town', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Postcode"
                    value={lead.customer_postcode || ''}
                    onChange={(e) => handleInputChange('customer_postcode', e.target.value)}
                    error={!!postcodeError}
                    helperText={postcodeError}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error': {
                          '& fieldset': {
                            borderColor: 'error.main',
                          },
                        },
                      },
                    }}
                  />
                </Box>
                
                {/* Map */}
                {mapCoordinates && (
                  <Box sx={{ 
                    flex: '0 0 45%', 
                    height: { xs: 300, md: 400 }, 
                    minHeight: 400,
                    borderRadius: 1, 
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.12)'
                  }}>
                    <MapContainer
                      key={`${mapCoordinates[0]}-${mapCoordinates[1]}`}
                      center={mapCoordinates}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                    >
                      <MapUpdater coordinates={mapCoordinates} />
                      <TileLayer
                        attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, HERE, Garmin, USGS, NGA'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                      />
                      <Marker position={mapCoordinates}>
                        <Popup>
                          {lead.customer_first_name} {lead.customer_last_name}<br />
                          {[lead.customer_address_1, lead.customer_address_2, lead.customer_town, lead.customer_postcode].filter(Boolean).join(', ')}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Notes - Full Width */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Notes color="primary" />
                Notes & Comments
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={lead.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                disabled={isFieldsLocked}
                placeholder="Add any additional notes or comments about this lead..."
              />
            </CardContent>
          </Card>
        </Box>

        {/* Order Details - HIDDEN */}
        {false && (
        <Box>
            <Accordion
              expanded={orderDetailsExpanded}
              onChange={() => setOrderDetailsExpanded(!orderDetailsExpanded)}
              sx={{
                boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                mb: 3,
                '&:before': { display: 'none' },
                '&.MuiAccordion-root': {
                  borderRadius: 2
                },
                '&.MuiAccordion-root:first-of-type': {
                  borderRadius: 2
                },
                '&.MuiAccordion-root:last-of-type': {
                  borderRadius: 2
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">
                    Order Details
                  </Typography>
                  <Badge badgeContent={orderItems.length} color="primary">
                    <Typography variant="body2" color="text.secondary">
                      {orderItems.length} item{orderItems.length !== 1 ? 's' : ''}
                    </Typography>
                  </Badge>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }} onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="contained"
                    size="small"
                    color="info"
                    startIcon={<Description />}
                    onClick={() => navigate(`/leads/${id}/order-build`)}
                  >
                    Build Order
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => setIssueOrderModalOpen(true)}
                  >
                    Issue Order
                  </Button>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                {/* Order Metadata */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Order Information
                  </Typography>
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Order Number"
                      value={lead.order_number || ''}
                      onChange={(e) => handleInputChange('order_number', e.target.value)}
                      disabled={isFieldsLocked}
                      placeholder="Enter order number"
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <TextField
                      label="Total Material Cost"
                      value={(lead.material_cost !== null && lead.material_cost !== undefined) ? String(lead.material_cost) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow typing without formatting
                        setLead({ ...lead, material_cost: value as any });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        console.log('LeadEdit Material Cost onBlur - raw value:', value);
                        const numValue = value ? parseFloat(value) : null;
                        console.log('LeadEdit Material Cost - parsed value:', numValue);
                        handleInputChange('material_cost', numValue);
                      }}
                      disabled={isFieldsLocked}
                      placeholder="0.00"
                      size="small"
                      type="text"
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: '[0-9]*\\.?[0-9]*'
                      }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>£</Typography>,
                      }}
                      sx={{ width: '200px' }}
                    />
                  </Box>
                  <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Supplier delivery date</Typography>
                      <Typography variant="body2">
                        {lead.date_required ? new Date(lead.date_required as string).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Customer delivery date</Typography>
                      <Typography variant="body2">
                        {lead.customer_delivery_date ? new Date(lead.customer_delivery_date as string).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Door Style</Typography>
                      <Typography variant="body2">{lead.door_style || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Worktop Style</Typography>
                      <Typography variant="body2">{lead.worktop_style || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Worktop Make</Typography>
                      <Typography variant="body2">{lead.worktop_make || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Colour</Typography>
                      <Typography variant="body2">{lead.item_colour || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Back Colour</Typography>
                      <Typography variant="body2">{lead.back_colour || '-'}</Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Order Items Table */}
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="center">HxW</TableCell>
                        <TableCell align="center">Type</TableCell>
                        <TableCell align="center">L</TableCell>
                        <TableCell align="center">Drillings</TableCell>
                        <TableCell align="center">R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map((item, index) => {
                        const isWorktopItem = item.primary_product === 'Worktops';

                        if (isWorktopItem) {
                          return (
                            <TableRow key={item.id || index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                              <TableCell>{item.supplier_name || '-'}</TableCell>
                              <TableCell>{item.category_name || '-'}</TableCell>
                              <TableCell colSpan={6}>{item.description || '-'}</TableCell>
                            </TableRow>
                          );
                        }

                        return (
                          <TableRow key={item.id || index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                            <TableCell>{item.supplier_name || '-'}</TableCell>
                            <TableCell>{item.category_name || '-'}</TableCell>
                            <TableCell align="center">{item.qty || '-'}</TableCell>
                            <TableCell align="center">{item.hxw || '-'}</TableCell>
                            <TableCell align="center">{item.type || '-'}</TableCell>
                            <TableCell align="center">{item.l || '-'}</TableCell>
                            <TableCell align="center">{item.drillings || '-'}</TableCell>
                            <TableCell align="center">{item.r || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Order Detail Notes */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Order Detail Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={lead.order_detail_notes || ''}
                    onChange={(e) => handleInputChange('order_detail_notes', e.target.value)}
                    disabled={isFieldsLocked}
                    placeholder="Add any additional notes or special instructions for this order..."
                    variant="outlined"
                    sx={{ 
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
        </Box>
        )}

        {/* Financial Information - Full Width - HIDDEN */}
        {false && (
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Financial Information
                </Typography>
                {id && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePaymentRequestClick}
                    size="small"
                  >
                    Email Payment Request
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={2} flexDirection="column">
                {/* Material Cost and Project Cost on same row */}
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Total Material Cost"
                      type="number"
                      value={lead.material_cost || 0}
                      onChange={(e) => handleInputChange('material_cost', parseFloat(e.target.value) || 0)}
                      disabled={isFieldsLocked}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                      }}
                    />
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'primary.lighter', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      height: '56px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Project Cost: £{calculateProjectCost().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Deposit and Balance */}
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Deposit Taken"
                      type="number"
                      value={lead.deposit_amount || 0}
                      onChange={(e) => handleInputChange('deposit_amount', parseFloat(e.target.value) || 0)}
                      disabled={isFieldsLocked}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                      }}
                    />
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: calculateBalance() > 0 ? 'warning.lighter' : 'success.lighter',
                      borderRadius: 1,
                      height: '56px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Balance Remaining: £{calculateBalance().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        )}

        {/* Additional Services - Full Width - HIDDEN */}
        {false && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Services
              </Typography>
              <Box display="flex" gap={2} flexDirection="column">
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Fitter Name"
                      value={lead.fitter_name || ''}
                      onChange={(e) => handleInputChange('fitter_name', e.target.value)}
                      disabled={isFieldsLocked}
                    />
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Fitter Cost"
                      type="number"
                      value={lead.fitter_balance || 0}
                      onChange={(e) => handleInputChange('fitter_balance', parseFloat(e.target.value) || 0)}
                      disabled={isFieldsLocked}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                      }}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Gas Fitter Name"
                      value={lead.gas_fitter_name || ''}
                      onChange={(e) => handleInputChange('gas_fitter_name', e.target.value)}
                      disabled={isFieldsLocked}
                    />
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Gas Fitter Cost"
                      type="number"
                      value={lead.gas_fitter_balance || 0}
                      onChange={(e) => handleInputChange('gas_fitter_balance', parseFloat(e.target.value) || 0)}
                      disabled={isFieldsLocked}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        )}

        {/* Pictures Section - Matches ProjectDetail styling */}
        {quotationDocuments.filter((doc: any) =>
          doc.original_name?.startsWith('Picture') ||
          (doc.mime_type?.startsWith('image/') && !doc.original_name?.startsWith('Quotation'))
        ).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoCamera color="primary" />
                Pictures
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {quotationDocuments.filter((doc: any) =>
                  doc.original_name?.startsWith('Picture') ||
                  (doc.mime_type?.startsWith('image/') && !doc.original_name?.startsWith('Quotation'))
                ).map((doc: any) => (
                  <Box
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 2 }
                    }}
                  >
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                      onClick={() => setSelectedDocument({
                        ...doc,
                        viewUrl: doc.source_table === 'project_photos'
                          ? (doc.fileUrl || `/uploads/${doc.file_path}`)
                          : `/api/documents/view/${doc.id}`,
                        mime_type: doc.mime_type || 'image/jpeg'
                      })}
                    >
                      <img
                        src={doc.source_table === 'project_photos'
                          ? (doc.fileUrl || `/uploads/${doc.file_path}`)
                          : `/api/documents/view/${doc.id}`}
                        alt={doc.filename || doc.original_name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', doc.file_path);
                          (e.target as HTMLImageElement).src = `/api/documents/view/${doc.id}`;
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                        }}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteQuotation(doc.id, doc.source_table);
                        }}
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 48,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                        }}
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDocForEmail(doc);
                          setEmailModalOpen(true);
                        }}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" noWrap>
                        {doc.original_name || doc.filename}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Documents (Quotations) Section */}
        {quotationDocuments.filter((doc: any) =>
          doc.original_name?.startsWith('Quotation') ||
          (!doc.mime_type?.startsWith('image/') && !doc.original_name?.startsWith('Picture'))
        ).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description color="primary" />
                Documents (Quotations)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {quotationDocuments.filter((doc: any) =>
                  doc.original_name?.startsWith('Quotation') ||
                  (!doc.mime_type?.startsWith('image/') && !doc.original_name?.startsWith('Picture'))
                ).map((doc: any) => (
                  <Box
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: 2,
                        '& .delete-btn': {
                          opacity: 1
                        },
                        '& .email-btn': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    {/* Delete button */}
                    <IconButton
                      className="delete-btn"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteQuotation(doc.id, doc.source_table);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>

                    {/* Email button for non-supplier order documents */}
                    {!(doc.original_name?.includes('Supplier Order') || doc.filename?.includes('SupplierOrder')) && (
                      <IconButton
                        className="email-btn"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDocForEmail(doc);
                          setEmailModalOpen(true);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 44,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '&:hover': {
                            bgcolor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    )}

                    {/* Document thumbnail/preview */}
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // For Word documents, open in modal with download button
                        if (doc.filename?.toLowerCase().endsWith('.docx') ||
                            doc.filename?.toLowerCase().endsWith('.doc') ||
                            doc.original_name?.toLowerCase().endsWith('.docx') ||
                            doc.original_name?.toLowerCase().endsWith('.doc')) {
                          setSelectedDocument({
                            ...doc,
                            viewUrl: `/api/documents/download/${doc.id}`,
                            mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            isWordDoc: true
                          });
                        } else {
                          // For other documents (PDFs, images), open normally
                          setSelectedDocument({
                            ...doc,
                            viewUrl: `/api/documents/view/${doc.id}`,
                            mime_type: doc.mime_type || (doc.filename?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg')
                          });
                        }
                      }}
                    >
                      {doc.mime_type?.startsWith('image/') ? (
                        <img
                          src={`/api/documents/view/${doc.id}`}
                          alt={doc.filename}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : doc.mime_type === 'application/pdf' ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <PictureAsPdf sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            PDF Document
                          </Typography>
                        </Box>
                      ) : doc.filename?.toLowerCase().match(/\.(doc|docx)$/) || doc.original_name?.toLowerCase().match(/\.(doc|docx)$/) ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Description sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            Word Document
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <Description sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            {doc.filename?.split('.').pop()?.toUpperCase() || 'Document'}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Document info */}
                    <Box sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={doc.original_name || doc.filename}>
                            {doc.original_name || doc.filename}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        {/* Send to Supplier button for Supplier Order documents */}
                        {(doc.original_name?.includes('Supplier Order') || doc.filename?.includes('SupplierOrder')) && (
                          <Tooltip title={
                            doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id
                              ? `Sent ${doc.supplier_sent_at ? new Date(doc.supplier_sent_at).toLocaleDateString() : 'just now'}`
                              : "Send to Supplier"
                          }>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color={(doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id) ? "success" : "primary"}
                                disabled={sendingToSupplier === doc.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenSupplierSendModal(doc);
                                }}
                                startIcon={sendingToSupplier === doc.id ? <CircularProgress size={14} color="inherit" /> :
                                          (doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id) ? <CheckCircle /> : <LocalShipping />}
                                sx={{ minWidth: 'auto', fontSize: '0.6rem', py: 0.3, px: 0.8, ml: 1, whiteSpace: 'nowrap' }}
                              >
                                {sendingToSupplier === doc.id ? 'Sending...' :
                                 (doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id)
                                   ? `Sent to Supplier on ${doc.supplier_sent_at ? new Date(doc.supplier_sent_at).toLocaleDateString() : new Date().toLocaleDateString()}`
                                   : 'Send to Supplier'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Actions Section */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History color="primary" />
                  Actions & Activity
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowActionForm(!showActionForm)}
                >
                  Add Action
                </Button>
              </Box>

              {/* Add Action Form */}
              {showActionForm && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Action Type</InputLabel>
                        <Select
                          value={newAction.action_type || 'Call'}
                          label="Action Type"
                          onChange={(e) => setNewAction({ ...newAction, action_type: e.target.value })}
                          size="small"
                        >
                          <MenuItem value="Call">Call</MenuItem>
                          <MenuItem value="Email">Email</MenuItem>
                          <MenuItem value="Home Visit">Home Visit</MenuItem>
                          <MenuItem value="Showroom Visit">Showroom Visit</MenuItem>
                          <MenuItem value="Quote">Quote Sent</MenuItem>
                          <MenuItem value="Follow-up">Follow-up</MenuItem>
                          <MenuItem value="Note">Note</MenuItem>
                          <MenuItem value="Order Issued">Order Issued</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Date"
                        type="date"
                        value={newAction.action_date || new Date().toISOString().split('T')[0]}
                        onChange={(e) => setNewAction({ ...newAction, action_date: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Time"
                        type="time"
                        value={newAction.action_time || new Date().toTimeString().slice(0, 5)}
                        onChange={(e) => setNewAction({ ...newAction, action_time: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    
                    {/* Show Assigned To dropdown for Home Visit and Showroom Visit */}
                    {(newAction.action_type === 'Home Visit' || newAction.action_type === 'Showroom Visit') && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Assigned To</InputLabel>
                        <Select
                          value={newAction.assigned_to || '21'}
                          label="Assigned To"
                          onChange={(e) => setNewAction({ ...newAction, assigned_to: e.target.value })}
                        >
                          {users.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={2}
                      value={newAction.description || ''}
                      onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                      placeholder="Add details about this action..."
                      size="small"
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setShowActionForm(false);
                          setNewAction({
                            action_type: 'Call',
                            description: '',
                            action_date: new Date().toISOString().split('T')[0],
                            action_time: new Date().toTimeString().slice(0, 5),
                            completed: false,
                            assigned_to: '21',
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={async () => {
                          if (newAction.description) {
                            const savedAction = await saveAction(newAction);
                            if (savedAction) {
                              setShowActionForm(false);
                              setNewAction({
                                action_type: 'Call',
                                description: '',
                                action_date: new Date().toISOString().split('T')[0],
                                action_time: new Date().toTimeString().slice(0, 5),
                                completed: false,
                                assigned_to: '21',
                              });
                            }
                          }
                        }}
                        disabled={!newAction.description}
                      >
                        Add Action
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Actions List */}
              <Box>
                {actions.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                    No actions recorded yet
                  </Typography>
                ) : (
                  <>
                    {actions
                      .slice(actionsPage * actionsPerPage, actionsPage * actionsPerPage + actionsPerPage)
                      .map((action) => (
                        <Box
                          key={action.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            border: '1px solid',
                            borderColor: action.action_type === 'Order Issued' ? '#4caf50' : 'divider',
                            borderRadius: 1,
                            backgroundColor: action.action_type === 'Order Issued' ? '#e8f5e9' : 'transparent',
                            '&:hover': {
                              bgcolor: action.action_type === 'Order Issued' ? '#c8e6c9' : 'grey.50'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                  label={action.action_type}
                                  size="small"
                                  color={action.action_type === 'Order Issued' ? 'success' : 'primary'}
                                  variant={action.action_type === 'Order Issued' ? 'filled' : 'outlined'}
                                  sx={{
                                    ...(action.action_type === 'Order Issued' && {
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    })
                                  }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                  <CalendarToday sx={{ fontSize: 16 }} />
                                  <Typography variant="caption">
                                    {new Date(action.action_date).toLocaleDateString()}
                                  </Typography>
                                  <AccessTime sx={{ fontSize: 16, ml: 1 }} />
                                  <Typography variant="caption">
                                    {action.action_time}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body2">{action.description}</Typography>
                              {action.user_name && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                  Added by {action.user_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))
                    }

                    {/* Pagination */}
                    {actions.length > actionsPerPage && (
                      <TablePagination
                        component="div"
                        count={actions.length}
                        page={actionsPage}
                        onPageChange={(event, newPage) => setActionsPage(newPage)}
                        rowsPerPage={actionsPerPage}
                        rowsPerPageOptions={[actionsPerPage]}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count} actions`}
                      />
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

      </Box>

      {/* Postcode Lookup Dialog */}
      <Dialog open={postcodeDialogOpen} onClose={() => setPostcodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Postcode Lookup</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Enter Postcode"
              value={postcodeSearch}
              onChange={(e) => setPostcodeSearch(e.target.value)}
              placeholder="e.g. BL1 1AA"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePostcodeLookup();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handlePostcodeLookup}
              disabled={loadingAddresses || !postcodeSearch.trim()}
            >
              {loadingAddresses ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Search Addresses'
              )}
            </Button>
            
            {addressOptions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select an address:
                </Typography>
                <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {addressOptions.map((address, index) => (
                    <ListItem
                      key={index}
                      component="div"
                      onClick={() => handleAddressSelect(address)}
                      sx={{
                        cursor: 'pointer',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText
                        primary={address.address1}
                        secondary={`${address.postTown}, ${address.county} ${address.postcode}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostcodeDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Start Project Dialog */}
      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Start New Project
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Create a new project for this won lead. This will track the project execution and completion.
            </Typography>
            
            <TextField
              fullWidth
              label="Project Title"
              value={projectForm.title}
              onChange={(e) => setProjectForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />

            <TextField
              fullWidth
              label="Order Number"
              value={projectForm.order_number}
              onChange={(e) => setProjectForm(prev => ({ ...prev, order_number: e.target.value }))}
              required
              error={!projectForm.order_number}
              helperText={!projectForm.order_number ? "Order number is required - please set one before creating the project" : "This links to the Order Details section"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: !projectForm.order_number ? 'error.main' : undefined,
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Project Description"
              multiline
              rows={3}
              value={projectForm.description}
              onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the project scope and requirements..."
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Project start date"
                type="date"
                value={projectForm.start_date}
                onChange={(e) => setProjectForm(prev => ({ ...prev, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Target project end date"
                type="date"
                value={projectForm.end_date}
                onChange={(e) => setProjectForm(prev => ({ ...prev, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Project Balance Remaining"
              type="number"
              value={projectForm.budget}
              disabled
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                readOnly: true,
              }}
              helperText="Calculated as: Material Cost - Deposit Taken"
            />
            
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained"
            disabled={creatingProject || !projectForm.title || !projectForm.order_number}
            startIcon={creatingProject ? <CircularProgress size={20} color="inherit" /> : <Work />}
          >
            {creatingProject ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Prompt Dialog */}
      <Dialog open={showEmailPrompt} onClose={() => {
        setShowEmailPrompt(false);
        setTempCustomerEmail('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="error" />
            Email Address Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom color="error">
              No email address found for this customer.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Please enter the customer's email address to send the payment request.
            </Typography>
            
            <TextField
              fullWidth
              label="Customer Email Address"
              type="email"
              value={tempCustomerEmail}
              onChange={(e) => setTempCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleEmailPromptSubmit();
                }
              }}
              helperText="This email will be saved to the customer's record"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowEmailPrompt(false);
            setTempCustomerEmail('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleEmailPromptSubmit}
            disabled={!tempCustomerEmail}
          >
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Request Dialog */}
      <Dialog open={showPaymentRequest} onClose={() => setShowPaymentRequest(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment color="primary" />
            Email Payment Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Sending payment request to:
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {`${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.trim() || lead.customer_name || 'Customer'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {lead.customer_email || 'No email address'}
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Deposit Amount"
              type="number"
              value={paymentRequestAmount}
              onChange={(e) => setPaymentRequestAmount(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
              }}
              helperText="Enter the deposit amount required"
              autoFocus
              inputProps={{
                min: 0,
                step: 0.01
              }}
            />
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="caption" color="info.main">
                <strong>Note:</strong> This will create a Stripe payment link and email it to the customer. 
                The customer will be able to pay securely online using their credit/debit card.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowPaymentRequest(false);
            setPaymentRequestAmount('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={sendingPaymentRequest ? <CircularProgress size={20} color="inherit" /> : <Send />}
            onClick={handleSendPaymentRequest}
            disabled={sendingPaymentRequest || !paymentRequestAmount || parseFloat(paymentRequestAmount) <= 0 || !lead.customer_email}
          >
            {sendingPaymentRequest ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quotation Upload Dialog */}
      <Dialog open={showQuotationUpload} onClose={() => setShowQuotationUpload(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Quotation Form</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload a quotation document by taking a photo or selecting from your device. The lead status will automatically be updated to "Quoted" after upload.
            </Typography>
            
            <QuotationUpload
              leadId={parseInt(id!)}
              onUploadComplete={(files) => handleQuotationUpload(files)}
              uploading={uploadingQuotation}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuotationUpload(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Pictures Upload Dialog */}
      <Dialog open={showPicturesUpload} onClose={() => setShowPicturesUpload(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Pictures</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload pictures by taking photos or selecting from your device. You can upload multiple pictures at once.
            </Typography>
            
            <QuotationUpload
              leadId={parseInt(id!)}
              onUploadComplete={(files) => handlePicturesUpload(files)}
              uploading={uploadingPictures}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPicturesUpload(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={!!selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          setZoomLevel(1);
        }}
        maxWidth={false}
        fullWidth
      >
        <DialogContent sx={{ position: 'relative', padding: 0, backgroundColor: selectedDocument?.isWordDoc ? '#f5f5f5' : '#000', minHeight: '80vh' }}>
          {selectedDocument && (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
              {selectedDocument.isWordDoc ? (
                // Word document - show download button
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Description sx={{ fontSize: 80, color: '#1976d2', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
                    {selectedDocument.original_name || selectedDocument.filename}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                    This document cannot be previewed. Click below to download.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<GetApp />}
                    onClick={async () => {
                      // Use fetch + blob for better Android/mobile compatibility
                      try {
                        const url = selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`;
                        const response = await fetch(url);
                        const blob = await response.blob();

                        // Ensure filename has .docx extension
                        let filename = selectedDocument.original_name || selectedDocument.filename || 'document';
                        if (!filename.toLowerCase().endsWith('.docx') && !filename.toLowerCase().endsWith('.doc')) {
                          filename += '.docx';
                        }

                        // Create blob URL and trigger download
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = filename;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();

                        // Cleanup
                        setTimeout(() => {
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(blobUrl);
                        }, 100);

                        setSelectedDocument(null);
                      } catch (error) {
                        console.error('Download failed:', error);
                        // Fallback: open in new tab
                        window.open(`/api/documents/download/${selectedDocument.id}`, '_blank');
                      }
                    }}
                    sx={{ mt: 2 }}
                  >
                    Download Document
                  </Button>
                </Box>
              ) : selectedDocument.mime_type?.startsWith('image/') || !selectedDocument.filename?.toLowerCase().endsWith('.pdf') ? (
                <img
                  src={selectedDocument.viewUrl || `/api/documents/view/${selectedDocument.id}`}
                  alt={selectedDocument.original_name || selectedDocument.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    transform: `scale(${zoomLevel})`,
                    transition: 'transform 0.3s',
                  }}
                />
              ) : (
                <embed
                  src={selectedDocument.viewUrl || `/api/documents/view/${selectedDocument.id}`}
                  type="application/pdf"
                  width="100%"
                  height="600px"
                  style={{
                    minHeight: '80vh',
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Issue Order Modal */}
      <Dialog open={issueOrderModalOpen} onClose={() => setIssueOrderModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Order</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              You are about to send the orders to{' '}
              <strong>
                {(() => {
                  const uniqueSuppliers = new Set(orderItems.filter(item => item.supplier_id).map(item => item.supplier_id));
                  return uniqueSuppliers.size;
                })()}
              </strong>{' '}
              supplier{(() => {
                const count = new Set(orderItems.filter(item => item.supplier_id).map(item => item.supplier_id)).size;
                return count !== 1 ? 's' : '';
              })()}.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              This will generate a supplier order document for each supplier and save them to the lead documents.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOrderModalOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={async () => {
              setIssueOrderModalOpen(false);
              await handleIssueOrders();
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Document Modal */}
      <EmailDocumentModal
        open={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setSelectedDocForEmail(null);
        }}
        document={selectedDocForEmail}
        customerEmail={lead.customer_email}
        customerName={`${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.trim()}
        onSuccess={() => {
          showSuccess('Document emailed successfully');
        }}
      />

      {/* Email Details Modal */}
      <Dialog
        open={leadEmailModalOpen}
        onClose={() => setLeadEmailModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Customer Details via Email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Send comprehensive customer information with a location map to a team member via email.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedEmailUser}
                onChange={(e) => setSelectedEmailUser(e.target.value)}
                label="Select User"
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {usersWithEmail.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} - {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Message (Optional)"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder="Add any additional notes or comments..."
              variant="outlined"
            />

            {usersWithEmail.length === 0 && (
              <Typography variant="body2" color="warning.main">
                No users with email addresses found. Please add email addresses in user settings.
              </Typography>
            )}

            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Email will include:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Customer name and contact information<br/>
                • Full address with interactive map<br/>
                • Lead details and status<br/>
                • Service type and specifications<br/>
                • Pricing information<br/>
                • Recent activity timeline<br/>
                • Direct link to this lead
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLeadEmailModalOpen(false)}
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            color="primary"
            disabled={!selectedEmailUser || sendingEmail || usersWithEmail.length === 0}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : null}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Details Modal */}
      <Dialog 
        open={whatsappModalOpen} 
        onClose={() => setWhatsappModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Customer Details via WhatsApp</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Send comprehensive customer information to a team member via WhatsApp.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedWhatsappUser}
                onChange={(e) => setSelectedWhatsappUser(e.target.value)}
                label="Select User"
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {usersWithPhone.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} - {user.mobile_phone}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {usersWithPhone.length === 0 && (
              <Typography variant="body2" color="warning.main">
                No users with mobile phone numbers found. Please add mobile numbers in user settings.
              </Typography>
            )}

            {lead.customer_latitude && lead.customer_longitude && (
              <FormControlLabel
                control={
                  <Switch
                    checked={includeMap}
                    onChange={(e) => setIncludeMap(e.target.checked)}
                  />
                }
                label="Include map screenshot"
              />
            )}

            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Message will include:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Customer contact details</li>
                <li>Lead status and value</li>
                <li>Requirements and notes</li>
                <li>Recent actions</li>
                {includeMap && lead.customer_latitude && <li>Location map</li>}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendWhatsApp}
            variant="contained"
            color="success"
            disabled={!selectedWhatsappUser || sendingWhatsapp || usersWithPhone.length === 0}
            startIcon={sendingWhatsapp ? <CircularProgress size={20} /> : null}
          >
            {sendingWhatsapp ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Won - Start Project Prompt Dialog */}
      <Dialog
        open={showWonProjectPrompt}
        onClose={() => setShowWonProjectPrompt(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Start a Project?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This lead has been marked as won. Would you like to start a project for this customer?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Customer:</strong> {lead.customer_first_name} {lead.customer_last_name}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Title:</strong> {lead.title}
            </Typography>
            <Typography variant="subtitle2">
              <strong>Value:</strong> £{lead.estimated_value || 0}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowWonProjectPrompt(false)}
          >
            Not Now
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowWonProjectPrompt(false);
              handleStartProject();
            }}
          >
            Start Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send to Supplier Confirmation Modal */}
      <Dialog
        open={supplierSendModalOpen}
        onClose={() => {
          setSupplierSendModalOpen(false);
          setSupplierSendDoc(null);
          setSupplierSendInfo(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping color="primary" />
          Send to Supplier
        </DialogTitle>
        <DialogContent>
          {loadingSupplierInfo ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : supplierSendInfo ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Please confirm you want to send this order:
              </Typography>

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Supplier:</strong> {supplierSendInfo.supplierName}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>To:</strong> {supplierSendInfo.supplierEmail}
                </Typography>
                {supplierSendInfo.ccEmails.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>CC:</strong> {supplierSendInfo.ccEmails.join(', ')}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Attachment:</strong> {supplierSendInfo.fileName}
                </Typography>
              </Box>

              {supplierSendInfo.supplierEmail === 'No email found' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No email address found for this supplier. Please add an email in the supplier settings.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSupplierSendModalOpen(false);
            setSupplierSendDoc(null);
            setSupplierSendInfo(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSendToSupplier}
            disabled={loadingSupplierInfo || supplierSendInfo?.supplierEmail === 'No email found'}
            startIcon={<Send />}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Order Sent Success Modal */}
      <Dialog
        open={supplierSentModalOpen}
        onClose={() => setSupplierSentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
            Order Sent Successfully!
          </Typography>
          {supplierSentDetails && (
            <Box sx={{ mt: 3, textAlign: 'left', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Sent to:</strong> {supplierSentDetails.sentTo}
              </Typography>
              {supplierSentDetails.ccTo.length > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>CC'd:</strong> {supplierSentDetails.ccTo.join(', ')}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Attachment:</strong> {supplierSentDetails.fileName}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            color="success"
            onClick={() => setSupplierSentModalOpen(false)}
            size="large"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}